import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Bell, BellRing, Send, Settings, CheckCircle, Clock,
  AlertCircle, MessageSquare, CreditCard, Award, Calendar,
  Megaphone, Users, RefreshCw, Mail, Smartphone, Eye, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_TYPES = {
  general: { label: 'General', icon: Bell, color: '#6B7280' },
  payment: { label: 'Paiement', icon: CreditCard, color: '#10B981' },
  grade: { label: 'Note', icon: Award, color: '#3B82F6' },
  announcement: { label: 'Annonce', icon: Megaphone, color: '#8B5CF6' },
  deadline: { label: 'Echeance', icon: Calendar, color: '#F59E0B' },
  exam: { label: 'Examen', icon: AlertCircle, color: '#EF4444' },
  message: { label: 'Message', icon: MessageSquare, color: '#0F2C59' }
};

export const NotificationsPage = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);

  const isAdmin = ['super_admin', 'co_admin', 'rector', 'vice_rector_academic', 'sg_academic',
    'sg_admin', 'dean', 'department_head', 'teacher'].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      const [notifsRes, statsRes, prefsRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications?limit=50`),
        axios.get(`${API_URL}/api/notifications/stats`),
        axios.get(`${API_URL}/api/notifications/preferences`)
      ]);
      setNotifications(notifsRes.data);
      setStats(statsRes.data);
      setPreferences(prefsRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('Toutes les notifications marquees comme lues');
    } catch {}
  };

  if (loading) return <LoadingSkel />;

  const unread = notifications.filter(n => !n.is_read);
  const read = notifications.filter(n => n.is_read);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Centre de notifications</h1>
          <p className="text-sm text-gray-500 mt-1">Gerez vos alertes et communications</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Actualiser</Button>
          <Button variant="outline" size="sm" onClick={() => setShowPrefs(true)} data-testid="prefs-btn">
            <Settings className="w-4 h-4 mr-2" />Preferences
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowSend(true)} data-testid="send-notif-btn">
              <Send className="w-4 h-4 mr-2" />Envoyer
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Bell className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{stats?.total || 0}</p><p className="text-xs text-gray-500">Total</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><BellRing className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold">{stats?.unread || 0}</p><p className="text-xs text-gray-500">Non lues</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold">{(stats?.total || 0) - (stats?.unread || 0)}</p><p className="text-xs text-gray-500">Lues</p></div>
        </CardContent></Card>
      </div>

      {/* Unread Alert */}
      {unread.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-md">
          <span className="text-sm text-amber-800">{unread.length} notification(s) non lue(s)</span>
          <Button size="sm" variant="outline" onClick={markAllRead} data-testid="mark-all-read">
            <Check className="w-3.5 h-3.5 mr-1" />Tout marquer comme lu
          </Button>
        </div>
      )}

      <Tabs defaultValue="all">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all">Toutes ({notifications.length})</TabsTrigger>
          <TabsTrigger value="unread">Non lues ({unread.length})</TabsTrigger>
          <TabsTrigger value="read">Lues ({read.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4 space-y-2">
          <NotifList notifications={notifications} onMarkRead={markAsRead} />
        </TabsContent>
        <TabsContent value="unread" className="mt-4 space-y-2">
          <NotifList notifications={unread} onMarkRead={markAsRead} />
        </TabsContent>
        <TabsContent value="read" className="mt-4 space-y-2">
          <NotifList notifications={read} />
        </TabsContent>
      </Tabs>

      {/* Send Dialog */}
      <SendNotificationDialog open={showSend} onOpenChange={setShowSend} onSuccess={fetchData} />

      {/* Preferences Dialog */}
      <PreferencesDialog open={showPrefs} onOpenChange={setShowPrefs} prefs={preferences} onUpdate={setPreferences} />
    </div>
  );
};

const NotifList = ({ notifications, onMarkRead }) => {
  if (!notifications?.length) {
    return (
      <Card><CardContent className="p-8 text-center text-gray-500">
        <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        Aucune notification
      </CardContent></Card>
    );
  }
  return notifications.map(n => {
    const type = NOTIF_TYPES[n.type] || NOTIF_TYPES.general;
    const TypeIcon = type.icon;
    return (
      <Card key={n.id} className={`transition-all ${!n.is_read ? 'border-l-4 bg-blue-50/30' : ''}`}
        style={!n.is_read ? { borderLeftColor: type.color } : {}} data-testid={`notif-${n.id}`}>
        <CardContent className="p-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: type.color + '15' }}>
            <TypeIcon className="w-4 h-4" style={{ color: type.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-sm ${!n.is_read ? 'font-semibold' : 'font-medium'}`}>{n.title}</p>
              {!n.is_read && <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] text-gray-400">{timeAgo(n.created_at)}</span>
              <Badge variant="outline" className="text-[10px] py-0">{type.label}</Badge>
            </div>
          </div>
          {!n.is_read && onMarkRead && (
            <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={() => onMarkRead(n.id)}>
              <Eye className="w-4 h-4 text-gray-400" />
            </Button>
          )}
        </CardContent>
      </Card>
    );
  });
};

const SendNotificationDialog = ({ open, onOpenChange, onSuccess }) => {
  const [form, setForm] = useState({
    title: '', body: '', notification_type: 'announcement', target_type: 'university',
    target_id: '', channels: ['in_app', 'push']
  });
  const [loading, setLoading] = useState(false);

  const toggleChannel = (ch) => {
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(ch)
        ? prev.channels.filter(c => c !== ch)
        : [...prev.channels, ch]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/notifications/send`, form);
      toast.success(res.data.message);
      onOpenChange(false);
      setForm({ title: '', body: '', notification_type: 'announcement', target_type: 'university', target_id: '', channels: ['in_app', 'push'] });
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const upd = (f, v) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Envoyer une notification</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Titre</Label>
            <Input value={form.title} onChange={e => upd('title', e.target.value)} required data-testid="notif-title" placeholder="Ex: Rappel de paiement" />
          </div>
          <div>
            <Label>Message</Label>
            <textarea className="w-full p-2 border rounded-md text-sm min-h-[80px] focus:ring-2 focus:ring-[var(--tenant-primary)] focus:border-transparent"
              value={form.body} onChange={e => upd('body', e.target.value)} required data-testid="notif-body"
              placeholder="Contenu de la notification..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.notification_type} onValueChange={v => upd('notification_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(NOTIF_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destinataire</Label>
              <Select value={form.target_type} onValueChange={v => upd('target_type', v)}>
                <SelectTrigger data-testid="notif-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="university">Toute l'universite</SelectItem>
                  <SelectItem value="promotion">Promotion</SelectItem>
                  <SelectItem value="faculty">Faculte</SelectItem>
                  <SelectItem value="user">Utilisateur</SelectItem>
                  <SelectItem value="all">Tous (global)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {['user', 'promotion', 'faculty'].includes(form.target_type) && (
            <div>
              <Label>ID du destinataire</Label>
              <Input value={form.target_id} onChange={e => upd('target_id', e.target.value)}
                placeholder={`ID ${form.target_type}`} required />
            </div>
          )}
          <div>
            <Label className="mb-2 block">Canaux d'envoi</Label>
            <div className="flex gap-3">
              {[
                { key: 'in_app', label: 'In-App', icon: Bell },
                { key: 'push', label: 'Push', icon: BellRing },
                { key: 'sms', label: 'SMS', icon: Smartphone }
              ].map(ch => (
                <button key={ch.key} type="button"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-all ${
                    form.channels.includes(ch.key)
                      ? 'bg-[var(--tenant-primary)] text-white border-[var(--tenant-primary)]'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleChannel(ch.key)} data-testid={`channel-${ch.key}`}
                >
                  <ch.icon className="w-4 h-4" />{ch.label}
                </button>
              ))}
            </div>
            {form.channels.includes('sms') && (
              <p className="text-xs text-amber-600 mt-1">SMS sera simule (integration Africa's Talking en attente)</p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={loading} data-testid="send-notif-submit">
              {loading ? 'Envoi...' : 'Envoyer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PreferencesDialog = ({ open, onOpenChange, prefs, onUpdate }) => {
  const [localPrefs, setLocalPrefs] = useState(prefs || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (prefs) setLocalPrefs(prefs); }, [prefs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/notifications/preferences`, localPrefs);
      toast.success('Preferences sauvegardees');
      onUpdate(localPrefs);
      onOpenChange(false);
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const toggle = (key) => setLocalPrefs(prev => ({ ...prev, [key]: !prev[key] }));

  const prfItems = [
    { key: 'in_app', label: 'Notifications In-App', icon: Bell, desc: 'Alertes dans l\'application' },
    { key: 'push', label: 'Notifications Push', icon: BellRing, desc: 'Alertes sur votre appareil' },
    { key: 'sms', label: 'Notifications SMS', icon: Smartphone, desc: 'Alertes par SMS' },
    { key: 'grade_alerts', label: 'Alertes notes', icon: Award, desc: 'Quand une note est publiee' },
    { key: 'payment_alerts', label: 'Alertes paiement', icon: CreditCard, desc: 'Rappels de paiement' },
    { key: 'announcement_alerts', label: 'Annonces', icon: Megaphone, desc: 'Annonces de l\'universite' },
    { key: 'deadline_alerts', label: 'Echeances', icon: Calendar, desc: 'Dates limites importantes' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Preferences de notification</DialogTitle></DialogHeader>
        <div className="space-y-3" data-testid="notif-preferences">
          {prfItems.map(item => (
            <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
              <div className="flex items-center gap-3">
                <item.icon className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
              <Switch checked={!!localPrefs[item.key]} onCheckedChange={() => toggle(item.key)} data-testid={`pref-${item.key}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-prefs-btn">
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'A l\'instant';
  if (mins < 60) return `Il y a ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

const LoadingSkel = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-64" />
    <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div>
    <div className="h-64 bg-gray-200 rounded" />
  </div>
);

export default NotificationsPage;
